import React, { useRef, useState } from 'react';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import './AtomFeatures.css';

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
  {
    id: 1,
    title: "Fast, familiar, frictionless",
    desc: "No more high fees or long waits. Apps on Atom feel instant, cost pennies, and work with the wallets and tools you already know and love.",
    label: "// 001",
    tech: "// PERFORMANCE",
    navLabel: "FAST, FAMILIAR, FRICTIONLESS",
    // Gradiente vivo: Amarillo -> Púrpura -> Cyan
    gradient: "conic-gradient(from 225deg at 50% 50%, #F6FF70 0deg, #99A3FF 120deg, #60DFFF 240deg, #F6FF70 360deg)"
  },
  {
    id: 2,
    title: "Decentralized by design",
    desc: "Atom is built for safety and security—from decentralized consensus to the all-new RaptorCast, unlock the power of crypto without the fear.",
    label: "// 002",
    tech: "// DECENTRALIZATION",
    navLabel: "DECENTRALIZED BY DESIGN",
    // Gradiente vivo: Cyan -> Lavanda
    gradient: "radial-gradient(circle at center, #5FE0FF 0%, #B8B8FF 50%, #FFFFFF 75%)"
  },
  {
    id: 3,
    title: "Community at its core",
    desc: "The Atom community is growing fast. Whether you're here to trade, build, make friends or learn something new, there's a place for you.",
    label: "// 003",
    tech: "// COMMUNITY",
    navLabel: "COMMUNITY AT ITS CORE",
    // Gradiente vivo: Naranja -> Rosa
    gradient: "radial-gradient(circle at center, #FFC470 0%, #FF85C2 40%, #C8A8FF 80%, #FFFFFF 100%)"
  }
];

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const AtomFeatures = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const visualColRef = useRef<HTMLDivElement>(null);
  const magneticRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // 1. SCROLL
  useGSAP(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;

    if (!container || !wrapper) return;

    ScrollTrigger.create({
      trigger: container,
      start: "top top",
      end: () => {
        const containerHeight = container.offsetHeight || window.innerHeight * 4;
        const scrollDistance = Math.max(containerHeight - window.innerHeight, 0);
        return `+=${scrollDistance}`;
      },
      pin: wrapper,
      scrub: 0.5,
      onUpdate: (self) => {
        const progress = self.progress;
        const index = Math.min(
          Math.floor(progress * FEATURES.length),
          FEATURES.length - 1
        );
        setActiveIndex(index);
      }
    });
  }, { scope: containerRef });

  // 2. MOUSE
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!magneticRef.current || !visualColRef.current) return;

    const rect = visualColRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const screenCenter = window.innerWidth / 2;

    let targetMouseX = e.clientX;
    if (targetMouseX < screenCenter) targetMouseX = screenCenter;

    const deltaX = targetMouseX - centerX;
    const deltaY = e.clientY - centerY;
    const moveX = clamp(deltaX * 0.15, -30, 80);
    const moveY = clamp(deltaY * 0.15, -60, 60);

    gsap.to(magneticRef.current, { x: moveX, y: moveY, duration: 1, ease: "power3.out", overwrite: "auto" });
  };

  const handleMouseLeave = () => {
    gsap.to(magneticRef.current, { x: 0, y: 0, duration: 1.2, ease: "power3.out", overwrite: "auto" });
  };

  return (
    <div className="af-container" ref={containerRef}>

      <div className="af-grid-bg"></div>

      <div className="af-wrapper" ref={wrapperRef}>
        <div className="af-content">

          {/* COLUMNA IZQUIERDA (TEXTO + BORDES) */}
          <div className="af-text-col">

            {/* Contenido Superior Justificado a la Izquierda */}
            <div className="af-text-top-content">
              <div className="af-headline-wrapper">
                <div key={activeIndex} className="af-text-animate">
                  <h2 className="af-title">{FEATURES[activeIndex].title}</h2>
                  <p className="af-description">{FEATURES[activeIndex].desc}</p>
                </div>
              </div>
            </div>

            {/* Botón independiente, posicionado entre el texto y la lista de pasos */}
            <a className="thm-btn agency-btn af-doc-btn" href="https://atom-foundation.gitbook.io/atom-foundation-docs" target="_blank" rel="noopener noreferrer">
              <span className="text">READ DOCUMENTATION</span>
              <span className="arrow">
                <span className="arrow-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5.06592"
                      y="19.9785"
                      width="20.5712"
                      height="2.61221"
                      transform="rotate(-40.2798 5.06592 19.9785)"
                      fill="white"
                    />
                    <rect
                      x="7.97095"
                      y="7.24463"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 7.97095 7.24463)"
                      fill="white"
                    />
                    <rect
                      x="11.6523"
                      y="7.54834"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 11.6523 7.54834)"
                      fill="white"
                    />
                    <rect
                      x="15.334"
                      y="7.85205"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 15.334 7.85205)"
                      fill="white"
                    />
                    <rect
                      x="18.7119"
                      y="11.8374"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.7119 11.8374)"
                      fill="white"
                    />
                    <rect
                      x="18.4084"
                      y="15.52"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.4084 15.52)"
                      fill="white"
                    />
                    <rect
                      x="18.104"
                      y="19.2012"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.104 19.2012)"
                      fill="white"
                    />
                  </svg>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5.06592"
                      y="19.9785"
                      width="20.5712"
                      height="2.61221"
                      transform="rotate(-40.2798 5.06592 19.9785)"
                      fill="white"
                    />
                    <rect
                      x="7.97095"
                      y="7.24463"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 7.97095 7.24463)"
                      fill="white"
                    />
                    <rect
                      x="11.6523"
                      y="7.54834"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 11.6523 7.54834)"
                      fill="white"
                    />
                    <rect
                      x="15.334"
                      y="7.85205"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 15.334 7.85205)"
                      fill="white"
                    />
                    <rect
                      x="18.7119"
                      y="11.8374"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.7119 11.8374)"
                      fill="white"
                    />
                    <rect
                      x="18.4084"
                      y="15.52"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.4084 15.52)"
                      fill="white"
                    />
                    <rect
                      x="18.104"
                      y="19.2012"
                      width="2.61221"
                      height="2.61221"
                      transform="rotate(-40.2798 18.104 19.2012)"
                      fill="white"
                    />
                  </svg>
                </span>
              </span>
            </a>

            {/* Navegación Inferior con Línea Divisoria Superior */}
            <div className="af-steps-container">
              {FEATURES.map((feature, idx) => (
                <div
                  key={feature.id}
                  className={`af-step-item ${idx === activeIndex ? 'active' : ''}`}
                >
                  <span className="af-step-num">0{feature.id}</span>
                  <div className="af-step-line"></div>
                  <span className="af-step-label">{feature.navLabel}</span>
                </div>
              ))}
            </div>

          </div>

          {/* COLUMNA DERECHA */}
          <div
            className="af-visual-col"
            ref={visualColRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div className="af-visual-magnetic" ref={magneticRef}>

              <div className="af-corner af-corner-tl"></div>
              <div className="af-corner af-corner-tr"></div>
              <div className="af-corner af-corner-bl"></div>
              <div className="af-corner af-corner-br"></div>

              <div className="af-tech-label af-label-top">{FEATURES[activeIndex].label}</div>
              <div className="af-tech-label af-label-bot">{FEATURES[activeIndex].tech}</div>

              <div
                className="af-glow"
                style={{ background: FEATURES[activeIndex].gradient }}
              ></div>

              {/* FIGURA 1 */}
              <div className={`af-shape-container ${activeIndex === 0 ? 'active' : ''}`}>
                <svg className="af-shape-svg" viewBox="0 0 400 400">
                  <rect x="65" y="65" width="270" height="270" fill="none" stroke="#555" strokeWidth="1.5" strokeDasharray="5 5" opacity="1" className="af-dashed-anim" />
                  <rect x="80" y="80" width="240" height="240" fill="none" stroke="#111" strokeWidth="2" transform="rotate(45 200 200)" />
                  <rect x="115" y="115" width="170" height="170" fill="none" stroke="#111" strokeWidth="2" />
                  <rect x="105" y="105" width="190" height="190" fill="none" stroke="#555" strokeWidth="1.5" strokeDasharray="5 5" transform="rotate(45 200 200)" className="af-dashed-anim" />
                </svg>
              </div>

              {/* FIGURA 2 */}
              <div className={`af-shape-container ${activeIndex === 1 ? 'active' : ''}`}>
                <svg className="af-shape-svg" viewBox="0 0 400 400">
                  <polygon points="200,40 339,120 339,280 200,360 61,280 61,120" fill="none" stroke="#111" strokeWidth="1.5" />
                  <polygon points="360,200 280,339 120,339 40,200 120,61 280,61" fill="none" stroke="#111" strokeWidth="1.5" />
                  <polygon points="200,45 291,325 53,152 347,152 109,325" fill="none" stroke="#444" strokeWidth="1.2" strokeDasharray="6 6" className="af-dashed-anim" />
                  <polygon points="200,45 347,152 291,325 109,325 53,152" fill="none" stroke="#444" strokeWidth="1.2" strokeDasharray="6 6" className="af-dashed-anim" />
                </svg>
              </div>

              {/* FIGURA 3: ANILLO COMPLETO (Corregido) */}
              <div className={`af-shape-container ${activeIndex === 2 ? 'active' : ''}`}>
                <svg className="af-shape-svg" viewBox="0 0 400 400">
                  <g transform="rotate(-15 200 200)">
                    <circle cx="200" cy="200" r="135" fill="none" stroke="#111" strokeWidth="1.5" />
                    <circle cx="200" cy="200" r="90" fill="none" stroke="#111" strokeWidth="1.5" />
                    <circle cx="200" cy="200" r="60" fill="none" stroke="#333" strokeWidth="1.5" strokeDasharray="6 6" className="af-dashed-anim" />
                    {/* Elipse completa cerrada, sin cortes */}
                    <ellipse
                      cx="200" cy="200" rx="195" ry="60"
                      fill="none" stroke="#111" strokeWidth="1.5"
                    />
                  </g>
                </svg>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtomFeatures;